import { getProductFormLabel } from '@/lib/productFormData';
import { NumText } from "@/components/ui/num-text";
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Package, Star, ShieldCheck, MessageSquare, Lock } from 'lucide-react';
import ListingThumbnail from '@/components/ListingThumbnail';
import { formatNumber, formatCurrency, getCurrencySymbol, isNewListing } from '@/lib/utils';
import { formatListingQuantity, formatListingPrice, calculateListingTotal, formatUnitSize, formatPackCase } from '@/lib/uom';
import { PriceWithTooltip } from '@/components/listings/PriceWithTooltip';
import { ColumnTooltip } from '@/components/listings/ColumnTooltip';
import { getEPPCode } from '@/lib/eppUtils';
import type { COACompliance } from '@/lib/coaCategoryConfig';
import { getCompliancePassCount } from '@/lib/coaCategoryConfig';
import WatchButton from '@/components/watch/WatchButton';

interface ListingData {
  id: string;
  reference_id?: string;
  strain_name?: string | null;
  species?: string | null;
  quantity_grams?: number | null;
  price_per_gram?: number | null;
  dimension?: string | null;
  quantity_value?: number | null;
  quantity_unit?: string | null;
  unit_weight_g?: number | null;
  case_size?: number | null;
  units_per_pack?: number | null;
  packs_per_case?: number | null;
  price_value?: number | null;
  price_unit?: string | null;
  thc_percent?: number | null;
  cbd_percent?: number | null;
  terpene_percent?: number | null;
  product_form?: string | null;
  region?: string | null;
  harvest_date?: string | null;
  images?: string[] | null;
  currency?: string | null;
  epp_environment?: string | null;
  epp_product?: string | null;
  epp_process?: string | null;
  compliance?: COACompliance | null;
  lab_name?: string | null;
  is_featured?: boolean;
  created_at?: string | null;
  matchScore?: number;
  matchedCriteria?: string[];
}

interface ContactInfo {
  contact_phone?: string;
  contact_email?: string;
}

interface MenuListingCardProps {
  item: ListingData;
  variant: 'tile' | 'list';
  contactInfo?: ContactInfo;
  showMatchScore?: boolean;
  selectable?: boolean;
  selected?: boolean;
  disableActions?: boolean;
  /** Buyer surfaces show a Watch control so interest survives leaving the page. */
  showWatch?: boolean;
  /** Menu this card is shown on — recorded as the watch's provenance. */
  menuId?: string | null;
  /** Read-only guest mode: hides price and action buttons (e.g. menu invite preview). */
  readOnly?: boolean;
  onCardClick: (listingId: string) => void;
  onContactClick: (item: ListingData) => void;
  onSelect?: (id: string, event?: React.MouseEvent) => void;
  /** Owner mode (My Menu): replaces the buyer "I'm Interested" action with owner controls. */
  ownerActions?: React.ReactNode;
  /** Owner-only photo badges (e.g. Sourced). Never pass on buyer surfaces. */
  ownerImageBadges?: React.ReactNode;
}

const getScoreColor = (score: number): string => {
  if (score >= 80) return 'text-green-500 bg-green-500/10';
  if (score >= 60) return 'text-yellow-500 bg-yellow-500/10';
  if (score >= 40) return 'text-orange-500 bg-orange-500/10';
  if (score > 0) return 'text-red-400 bg-red-400/10';
  return 'text-muted-foreground bg-muted/50';
};

const MenuListingCard = ({
  item,
  variant,
  contactInfo,
  showMatchScore = false,
  selectable = false,
  selected = false,
  disableActions = false,
  showWatch = false,
  menuId = null,
  readOnly = false,
  onCardClick,
  onContactClick,
  onSelect,
  ownerActions,
  ownerImageBadges,
}: MenuListingCardProps) => {
  const cleanTestCount = getCompliancePassCount(item.compliance || null);
  const hasEPP = item.epp_environment || item.epp_product || item.epp_process;
  const hasSafetyBadge = cleanTestCount > 0;

  if (variant === 'list') {
    return (
      <div
        data-row-id={item.id}
        className={`relative border border-border rounded-lg p-3 bg-card hover:bg-muted/30 transition-colors cursor-pointer ${item.is_featured ? 'ring-2 ring-primary/20' : ''} ${selected ? 'ring-2 ring-primary' : ''}`}
        onClick={() => onCardClick(item.id)}
      >
        {/* Selection Checkbox */}
        {selectable && (
          <div className="absolute top-2 right-2 z-10" onClick={(e) => { e.stopPropagation(); onSelect?.(item.id, e); }}>
            <Checkbox checked={selected} onCheckedChange={() => {}} tabIndex={-1} />
          </div>
        )}
        {/* Left-edge flag NEW pill */}
        {isNewListing(item.created_at) && (
           <span className="absolute top-1 left-0 z-10 px-1.5 py-[3px] rounded-full bg-amber-400 text-black text-[7px] font-bold uppercase tracking-wide shadow-sm leading-none inline-flex items-center justify-center mx-[4px]">
            NEW
          </span>
        )}
        <div className="flex items-center gap-3">
          {/* Image */}
          <div className="w-10 h-10 flex-shrink-0 bg-muted rounded overflow-hidden">
            {item.images && item.images.length > 0 ? (
              <ListingThumbnail
                src={item.images[0]}
                alt={item.strain_name || 'Product'}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Package className="w-4 h-4 text-muted-foreground" />
              </div>
            )}
          </div>

          {/* Strain Name */}
          <div className="min-w-0 w-32 flex-shrink-0">
            <div className="flex items-center gap-1.5">
              {item.is_featured && <Star className="w-3 h-3 text-primary flex-shrink-0" />}
              <h3 className="font-medium truncate text-sm">
                {item.strain_name || 'Unnamed'}
              </h3>
              {item.species && (
                <span className="text-xs text-muted-foreground flex-shrink-0">
                  {item.species}
                </span>
              )}
            </div>
          </div>

          {/* EPP Code */}
          {hasEPP && (
            <div className="hidden sm:block flex-shrink-0">
              <span className="px-1.5 py-0.5 bg-muted rounded text-xs num-id text-muted-foreground">
                {getEPPCode(item.epp_environment, item.epp_product, item.epp_process)}
              </span>
            </div>
          )}

          {/* Product Form */}
          <div className="hidden md:block w-16 flex-shrink-0">
            <span className="text-xs text-muted-foreground">
              {item.product_form ? getProductFormLabel(item.product_form) : '—'}
            </span>
          </div>

          {/* Quantity */}
          <div className="hidden sm:block w-20 flex-shrink-0">
            <div className="whitespace-nowrap text-xs leading-tight">
              <ColumnTooltip column="quantity" listing={item} className="text-muted-foreground">
                <NumText role="num-cell">{formatListingQuantity(item)}</NumText>
              </ColumnTooltip>
              {formatUnitSize(item) && (
                <ColumnTooltip column="unitSize" listing={item} className="block text-xs text-muted-foreground">
                  {formatUnitSize(item)}
                </ColumnTooltip>
              )}
              {formatPackCase(item) && (
                <ColumnTooltip column="caseSize" listing={item} className="block text-xs text-muted-foreground">
                  {formatPackCase(item)}
                </ColumnTooltip>
              )}
            </div>
          </div>

          {/* THC */}
          <div className="hidden md:block w-16 flex-shrink-0">
            <span className="num-cell text-xs text-muted-foreground">
              {item.thc_percent ? `${formatNumber(item.thc_percent)}%` : '—'}
            </span>
          </div>

          {/* Terpenes */}
          <div className="hidden lg:block w-16 flex-shrink-0">
            <span className="num-cell text-xs text-muted-foreground">
              {item.terpene_percent ? `${formatNumber(item.terpene_percent)}%` : '—'}
            </span>
          </div>

          {/* Price */}
          <div className="w-24 flex-shrink-0">
            {(item.price_value ?? item.price_per_gram) != null && (item.price_value ?? item.price_per_gram)! > 0 ? (
              <div>
                <PriceWithTooltip listing={item} currencySymbol={getCurrencySymbol(item.currency)} className="whitespace-nowrap text-sm font-medium text-primary block">
                  <NumText role="num-cell">{formatListingPrice(item, getCurrencySymbol(item.currency))}</NumText>
                </PriceWithTooltip>
                {calculateListingTotal(item) && (
                  <p className="whitespace-nowrap text-xs text-muted-foreground">
                    <NumText role="num-cell">{`${getCurrencySymbol(item.currency)}${formatCurrency(calculateListingTotal(item))} total`}</NumText>
                  </p>
                )}
              </div>
            ) : (
              <span className="text-sm font-medium text-primary">Open to Offers</span>
            )}
          </div>

          {/* Safety Badge */}
          {hasSafetyBadge && (
            <div className="hidden lg:block flex-shrink-0">
              <Badge variant="verified" className="text-xs">
                <ShieldCheck className="w-2.5 h-2.5 mr-0.5" />
                {cleanTestCount}
              </Badge>
            </div>
          )}

          {/* Match Score */}
          {showMatchScore && (item.matchScore ?? 0) > 0 && (
            <div className={`num px-1.5 py-0.5 rounded text-xs font-medium flex-shrink-0 ${getScoreColor(item.matchScore)}`}>
              {Math.round(item.matchScore)}%
            </div>
          )}

          {/* Spacer */}
          <div className="flex-1" />

          {/* Actions */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <Button
              size="sm"
              variant="outline"
              className="text-xs"
              disabled={disableActions}
              onClick={(e) => {
                e.stopPropagation();
                onContactClick(item);
              }}
            >
              <MessageSquare className="w-3 h-3 mr-1" />
              <span className="hidden sm:inline">I'm Interested</span>
            </Button>
            {showWatch && !readOnly && <WatchButton watchType="listing" listingId={item.id} menuId={menuId} />}
          </div>
        </div>
      </div>
    );
  }

  // Tile variant (default)
  return (
    <Card
      data-row-id={item.id}
      className={`overflow-hidden hover:shadow-md transition-shadow cursor-pointer flex flex-col ${item.is_featured ? 'ring-2 ring-primary/20' : ''} ${selected ? 'ring-2 ring-primary' : ''}`}
      onClick={() => onCardClick(item.id)}
    >
      <CardContent className="p-0 flex flex-col flex-1">
        <div className="aspect-[4/3] sm:aspect-square bg-muted relative overflow-hidden">
          {/* Selection Checkbox */}
          {selectable && (
            <div className="absolute top-2 left-2 z-10 p-1 bg-background/80 backdrop-blur-sm rounded" onClick={(e) => { e.stopPropagation(); onSelect?.(item.id, e); }}>
              <Checkbox checked={selected} onCheckedChange={() => {}} tabIndex={-1} />
            </div>
          )}
          {item.images && item.images.length > 0 ? (
            <ListingThumbnail
              src={item.images[0]}
              alt={item.strain_name || 'Product'}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Package className="w-12 h-12 text-muted-foreground" />
            </div>
          )}
          {showWatch && !readOnly && (
            <div className="absolute top-2 right-2 z-10" onClick={(e) => e.stopPropagation()}>
              <WatchButton watchType="listing" listingId={item.id} menuId={menuId} className="h-9 w-9 rounded-full bg-background/90 backdrop-blur-sm shadow-sm border border-border" />
            </div>
          )}
          {/* Match Score Badge */}
          {showMatchScore && (item.matchScore ?? 0) > 0 && (
            <div className={`num absolute ${showWatch && !readOnly ? 'top-14' : 'top-2'} right-2 px-2 py-1 rounded-full text-xs font-medium ${getScoreColor(item.matchScore)}`}>
              {Math.round(item.matchScore)}%
            </div>
          )}
          {/* EPP Badge */}
          {hasEPP && (
            <div className="num-id absolute bottom-2 right-2 px-2 py-1 bg-background/90 backdrop-blur-sm rounded text-xs font-medium text-foreground border border-border">
              {getEPPCode(item.epp_environment, item.epp_product, item.epp_process)}
            </div>
          )}
          {isNewListing(item.created_at) && !item.is_featured && (
            <span className={`absolute top-2 ${selectable ? 'left-10' : 'left-2'} z-10 px-1.5 py-[3px] rounded-full bg-amber-400 text-black text-[7px] font-bold uppercase tracking-wide shadow-sm leading-none inline-flex items-center justify-center mx-[4px]`}>
              NEW
            </span>
          )}
          {isNewListing(item.created_at) && item.is_featured && (
            <span className={`absolute top-9 ${selectable ? 'left-10' : 'left-2'} z-10 px-1.5 py-[3px] rounded-full bg-amber-400 text-black text-[7px] font-bold uppercase tracking-wide shadow-sm leading-none inline-flex items-center justify-center mx-[4px]`}>
              NEW
            </span>
          )}
          {item.is_featured && (
            <Badge variant="default" className={`absolute top-2 ${selectable ? 'right-2' : 'left-2'}`}>
              <Star className="w-3 h-3 mr-1 fill-amber-400 text-amber-400" />
              Featured
            </Badge>
          )}
          {ownerImageBadges}
        </div>
        <div className="p-3 sm:p-4 flex flex-col flex-1">
          <h3 className="text-base sm:text-lg font-semibold leading-snug line-clamp-2 break-words" title={item.strain_name || undefined}>
            {item.strain_name || 'Unnamed'}
          </h3>
          {(item.species || item.product_form) && (
            <p className="text-xs text-muted-foreground truncate">
              {[item.species, item.product_form && getProductFormLabel(item.product_form)].filter(Boolean).join(' · ')}
            </p>
          )}
          <div className="mt-1.5 sm:mt-2 space-y-1 sm:space-y-1.5">
            <p className="text-xs sm:text-sm text-muted-foreground truncate">
              <ColumnTooltip column="quantity" listing={item}>
                <span className="font-semibold text-foreground"><NumText>{formatListingQuantity(item)}</NumText></span>
              </ColumnTooltip>
              {formatUnitSize(item) && <span> · <ColumnTooltip column="unitSize" listing={item}><NumText>{formatUnitSize(item)}</NumText></ColumnTooltip></span>}
              {formatPackCase(item) && <span> · <ColumnTooltip column="caseSize" listing={item}><NumText>{formatPackCase(item)}</NumText></ColumnTooltip></span>}
              {item.epp_environment && <span> · {item.epp_environment}</span>}
              {item.epp_process && <span> · {item.epp_process}</span>}
            </p>
            {(() => {
              const parts = [
                item.thc_percent && `THC ${formatNumber(item.thc_percent)}%`,
                item.terpene_percent && `Terp ${formatNumber(item.terpene_percent)}%`,
              ].filter(Boolean) as string[];
              const text = [parts.join(' · '), item.lab_name].filter(Boolean);
              return text.length > 0 ? (
                <p className="text-xs sm:text-sm text-muted-foreground truncate">
                  <NumText>{parts.join(' · ')}</NumText>
                  {item.lab_name && <span className="hidden sm:inline">{parts.length ? ' · ' : ''}{item.lab_name}</span>}
                </p>
              ) : null;
            })()}
          </div>
          {/* Safety Badge */}
          {hasSafetyBadge && (
            <div className="mt-1">
              <Badge variant="verified" className="text-xs">
                <ShieldCheck className="w-3 h-3 mr-1" />
                {cleanTestCount} Test{cleanTestCount > 1 ? 's' : ''} Passed
              </Badge>
            </div>
          )}
          <div className="mt-auto pt-2 sm:pt-3">
            {readOnly ? (
              <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Lock className="w-3 h-3" />
                Sign in for price & full details
              </p>
            ) : (
              <>
                <div className="flex flex-col gap-2 sm:block">
                {(item.price_value ?? item.price_per_gram) != null && (item.price_value ?? item.price_per_gram)! > 0 ? (
                  <div className="min-w-0 sm:mb-3">
                    <PriceWithTooltip listing={item} currencySymbol={getCurrencySymbol(item.currency)} className="text-sm sm:text-base font-semibold text-primary block">
                      <NumText role="num-metric">{formatListingPrice(item, getCurrencySymbol(item.currency))}</NumText>
                    </PriceWithTooltip>
                    {calculateListingTotal(item) && (
                      <p className="text-xs sm:text-sm text-muted-foreground truncate">
                        <NumText>{`${getCurrencySymbol(item.currency)}${formatCurrency(calculateListingTotal(item))} total`}</NumText>
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm sm:text-base font-semibold text-primary sm:mb-3 leading-tight">Open to Offers</p>
                )}
{ownerActions ?? (
                  <Button
                    size="sm"
                    variant="outline"
                    aria-label="I'm Interested"
                    className="h-10 w-full px-2 sm:h-9"
                    disabled={disableActions}
                    onClick={(e) => {
                      e.stopPropagation();
                      onContactClick(item);
                    }}
                  >
                    <MessageSquare className="w-4 h-4 mr-1.5 shrink-0" />
                    <span className="whitespace-nowrap">I'm Interested</span>
                  </Button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default MenuListingCard;
